// src/components/proposal/SolicitationUnderstanding.tsx
// Shows, in plain language, what the system actually understood from the
// solicitation + its attachments (the extracted SolicitationProfile). This is the
// "are we reading the RFQ correctly?" panel — review it before generating.

import React, { useState } from 'react';
import {
  FileSearch,
  ChevronDown,
  ChevronRight,
  Scale,
  ListChecks,
  ClipboardList,
  Users,
  ShieldCheck,
  DollarSign,
  AlertTriangle,
  FileStack,
} from 'lucide-react';
import { SolicitationProfile } from '../../services/orchestrated-proposal-service';

interface Props {
  profile: SolicitationProfile;
}

function Pill({ children, tone = 'slate' }: { children: React.ReactNode; tone?: 'slate' | 'blue' | 'amber' | 'emerald' | 'red' }) {
  const tones: Record<string, string> = {
    slate: 'bg-slate-100 text-slate-700',
    blue: 'bg-bw-primary-tint text-bw-primary',
    amber: 'bg-fit-moderate-bg text-fit-moderate',
    emerald: 'bg-success-bg text-success',
    red: 'bg-urgent-bg text-urgent',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

function Section({
  icon,
  title,
  count,
  children,
  defaultOpen = true,
}: {
  icon: React.ReactNode;
  title: string;
  count?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-bw-border first:border-t-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 py-3 text-left hover:bg-bw-surface-subtle/60 transition-colors"
      >
        <span className="text-slate-500">{icon}</span>
        <span className="text-sm font-semibold text-bw-ink flex-1">{title}</span>
        {typeof count === 'number' && (
          <span className="text-xs text-bw-muted">{count}</span>
        )}
        {open ? <ChevronDown className="w-4 h-4 text-bw-muted" /> : <ChevronRight className="w-4 h-4 text-bw-muted" />}
      </button>
      {open && <div className="pb-4 pl-7 pr-1">{children}</div>}
    </div>
  );
}

const has = (v: any) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);

export const SolicitationUnderstanding: React.FC<Props> = ({ profile }) => {
  if (!profile) return null;

  const v = profile.vehicle;
  const ev = profile.evaluation;
  const sub = profile.submission;
  const sec = profile.security;
  const pr = profile.pricing;

  const isCommercial = v?.isCommercialItem === true || ['12', '13'].includes(String(v?.farPart || ''));

  return (
    <div className="bg-bw-surface rounded-xl border shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-bw-surface-subtle border-b border-bw-border">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-slate-100 text-slate-600">
            <FileSearch className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-bw-ink">What we understood from the solicitation</h3>
            <p className="text-xs text-bw-muted mt-0.5">
              Auto-extracted from the RFQ and its attachments. This controls how the proposal is built —
              skim it and confirm it matches the documents before generating.
            </p>
          </div>
        </div>
        {has(profile.summary) && (
          <p className="text-sm text-bw-muted mt-3 leading-relaxed">{profile.summary}</p>
        )}
      </div>

      <div className="px-5">
        {/* Vehicle & scale */}
        {v && (has(v.type) || has(v.farPart) || has(v.guaranteedMinimum) || has(v.maximumValue) || has(v.periods) || has(v.sizeStandard)) && (
          <Section icon={<Scale className="w-4 h-4" />} title="Contract vehicle & scale">
            <div className="flex flex-wrap gap-2 mb-2">
              {has(v.type) && <Pill tone="blue">{v.type}</Pill>}
              {has(v.farPart) && <Pill tone={isCommercial ? 'emerald' : 'slate'}>FAR Part {v.farPart}{isCommercial ? ' · commercial' : ''}</Pill>}
            </div>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {has(v.guaranteedMinimum) && (<div className="flex justify-between gap-3"><dt className="text-bw-muted">Guaranteed minimum</dt><dd className="font-medium text-bw-ink">{v.guaranteedMinimum}</dd></div>)}
              {has(v.maximumValue) && (<div className="flex justify-between gap-3"><dt className="text-bw-muted">Maximum / ceiling</dt><dd className="font-medium text-bw-ink">{v.maximumValue}</dd></div>)}
              {has(v.sizeStandard) && (<div className="flex justify-between gap-3"><dt className="text-bw-muted">SBA size standard</dt><dd className="font-medium text-bw-ink">{v.sizeStandard}</dd></div>)}
              {has(v.orderingPeriod) && (<div className="flex justify-between gap-3"><dt className="text-bw-muted">Ordering period</dt><dd className="font-medium text-bw-ink">{v.orderingPeriod}</dd></div>)}
            </dl>
            {has(v.periods) && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(v.periods || []).map((p, i) => <Pill key={i}>{p.label}{p.months ? ` (${p.months}mo)` : ''}</Pill>)}
              </div>
            )}
          </Section>
        )}

        {/* Evaluation */}
        {ev && (has(ev.method) || has(ev.factorsInPriorityOrder) || has(ev.priceEvaluation)) && (
          <Section icon={<ListChecks className="w-4 h-4" />} title="How it's evaluated" count={ev.factorsInPriorityOrder?.length}>
            {has(ev.method) && <p className="text-sm text-bw-ink mb-2">{ev.method}</p>}
            <ol className="space-y-1.5">
              {(ev.factorsInPriorityOrder || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-bw-muted font-mono text-xs mt-0.5">{i + 1}.</span>
                  <span className="text-bw-ink">{f.name}</span>
                  {has(f.weight) && <Pill>{f.weight}</Pill>}
                  {f.isGate && <Pill tone="red">GATE</Pill>}
                </li>
              ))}
            </ol>
            {has(ev.nonPriceVsPrice) && <p className="text-xs text-bw-muted mt-2">{ev.nonPriceVsPrice}</p>}
            {has(ev.priceEvaluation) && <p className="text-xs text-bw-muted mt-1">Price: {ev.priceEvaluation}</p>}
          </Section>
        )}

        {/* Submission volumes */}
        {sub && (has(sub.volumes) || has(sub.pricingPlacementRule) || has(sub.dueDateLocal)) && (
          <Section icon={<FileStack className="w-4 h-4" />} title="Submission structure" count={sub.volumes?.length}>
            <div className="space-y-1.5">
              {(sub.volumes || []).map((vol, i) => (
                <div key={i} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-bw-ink">{vol.name}{vol.factor ? ` — ${vol.factor}` : ''}</span>
                  {has(vol.pageLimit) && <Pill tone="amber">{vol.pageLimit}</Pill>}
                </div>
              ))}
            </div>
            {has(sub.pricingPlacementRule) && <p className="text-xs text-fit-moderate mt-2">⚠ {sub.pricingPlacementRule}</p>}
            {has(sub.dueDateLocal) && <p className="text-xs text-bw-muted mt-1">Due: {sub.dueDateLocal}</p>}
          </Section>
        )}

        {/* PWS tasks */}
        {has(profile.pwsTasks) && (
          <Section icon={<ClipboardList className="w-4 h-4" />} title="PWS task tree" count={profile.pwsTasks!.length} defaultOpen={false}>
            <ul className="space-y-1.5">
              {(profile.pwsTasks || []).map((t, i) => (
                <li key={i} className="text-sm text-bw-ink">
                  <span className="font-medium">{t.number ? `${t.number} ` : ''}{t.title}</span>
                  {has(t.summary) && <span className="text-bw-muted"> — {t.summary}</span>}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Key personnel */}
        {has(profile.keyPersonnel) && (
          <Section icon={<Users className="w-4 h-4" />} title="Required key personnel" count={profile.keyPersonnel!.length}>
            <div className="space-y-2">
              {(profile.keyPersonnel || []).map((kp, i) => (
                <div key={i} className="text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-bw-ink">{kp.role}</span>
                    <Pill tone={kp.required === false ? 'slate' : 'emerald'}>{kp.required === false ? 'recommended' : 'required'}</Pill>
                  </div>
                  {has(kp.minQualifications) && <p className="text-bw-muted text-xs mt-0.5">{kp.minQualifications}</p>}
                  {has(kp.certifications) && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(kp.certifications || []).map((c, j) => <Pill key={j} tone="blue">{c}</Pill>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Technical constraints */}
        {has(profile.technicalConstraints) && (
          <Section icon={<AlertTriangle className="w-4 h-4" />} title="Mandatory technical constraints" count={profile.technicalConstraints!.length} defaultOpen={false}>
            <div className="flex flex-wrap gap-1.5">
              {(profile.technicalConstraints || []).map((c, i) => <Pill key={i} tone="amber">{c}</Pill>)}
            </div>
          </Section>
        )}

        {/* Security */}
        {sec && (has(sec.tier) || has(sec.clearance) || sec.phiAccess !== null && sec.phiAccess !== undefined || has(sec.trainings)) && (
          <Section icon={<ShieldCheck className="w-4 h-4" />} title="Security" defaultOpen={false}>
            <div className="flex flex-wrap gap-2">
              {has(sec.tier) && <Pill tone="slate">{sec.tier}</Pill>}
              {has(sec.clearance) && <Pill tone="slate">{sec.clearance}</Pill>}
              {sec.phiAccess === false && <Pill tone="emerald">No PHI access</Pill>}
              {sec.phiAccess === true && <Pill tone="amber">PHI access</Pill>}
              {(sec.trainings || []).map((t, i) => <Pill key={i} tone="blue">{t}</Pill>)}
            </div>
          </Section>
        )}

        {/* Pricing */}
        {pr && (has(pr.instrument) || has(pr.source) || pr.costBuildupProhibited !== null && pr.costBuildupProhibited !== undefined) && (
          <Section icon={<DollarSign className="w-4 h-4" />} title="Pricing artifact" defaultOpen={false}>
            <div className="space-y-1 text-sm text-bw-ink">
              {has(pr.instrument) && <p>{pr.instrument}</p>}
              {has(pr.source) && <p className="text-bw-muted text-xs">Source: {pr.source}</p>}
              {pr.costBuildupProhibited === true && <p className="text-xs text-success">Commercial pricing — firm unit/total prices per CLIN; no FAR Table 15-2 cost buildup.</p>}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};

export default SolicitationUnderstanding;
